'use client';

import { isFunctionValue, isStringValue } from '../lib/validation/primitives';


import { useEffect, useRef, useState } from 'react';
import { usePathname } from 'next/navigation';
import { emitClientEvent, waitForPostHog } from '@/lib/analytics/emit-client-event';
import {
  isFirstPartyAttributionHostname,
} from '@/lib/analytics/signup-source-config';
import { normalizeAttributionHostname } from '@engine/attribution';
import { scrubSensitiveUrl } from '@/lib/analytics/scrub-url';

const DEBOUNCE_MS = 2000;

/**
 * Sends page_view to the event pipeline (Slack, Mixpanel, etc.) on route change.
 * Debounced to avoid flooding on rapid navigation.
 * Deferred so it never blocks first render / LCP.
 */
export function TrackPageView() {
  const pathname = usePathname();
  const lastSent = useRef<string | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const boot = () => setReady(true);

    if (document.readyState === 'complete') {
      boot();
    } else {
      window.addEventListener('load', boot, { once: true });

      return () => window.removeEventListener('load', boot);
    }
  }, []);

  useEffect(() => {
    if (!ready) return;

    if (!pathname || !isStringValue(pathname)) return;

    if (lastSent.current === pathname) return;

    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      lastSent.current = pathname;
      timeoutRef.current = null;

      // Client-side GA4 page_view via gtag.js — this is what forms a real numeric GA4
      // session and derives the traffic-source channel (Organic/Direct/Referral/Paid)
      // from referrer + UTM + gclid. Server MP no longer sends page_view (providers/
      // ga4.ts skips it), so no double-count. No-ops silently if gtag is blocked.
      // SAFETY: The surrounding typed contract establishes the asserted representation.
      const w = window as Window & { gtag?: (...args: unknown[]) => void };

      if (isFunctionValue(w.gtag)) {
        w.gtag('event', 'page_view', {
          page_path: pathname,
          page_location: scrubSensitiveUrl(window.location.href),
          page_title: document.title,
        });
      }

      // Server pipeline (event_log / Mixpanel / Slack) — blocker-proof first-party copy.
      // emitClientEvent attaches clientId + sessionId; these rows are the
      // pre-signup half of every journey replay. Keep this session evidence
      // separate from the immutable first-touch signup cookie.
      const referrerHost = normalizeAttributionHostname(document.referrer);

      const externalReferrerHost =
        referrerHost && !isFirstPartyAttributionHostname(referrerHost, window.location.hostname)
          ? referrerHost
          : undefined;

      const pageView = {
        name: 'page_view',
        path: pathname,
        // GA4 attributes the page from page_location/page_title — send the full URL + title.
        page_location: scrubSensitiveUrl(window.location.href),
        page_title: document.title,
        ...(externalReferrerHost ? { referrer_host: externalReferrerHost } : {}),
      };

      // Capture now (the user may navigate during the wait), send once PostHog is up
      // so even a visit's first page view carries its PostHog session id.
      void waitForPostHog().then(() => emitClientEvent('/api/events/track', pageView));
    }, DEBOUNCE_MS);

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [pathname, ready]);

  return null;
}
